import { useEffect, useRef } from 'react'
import { logger } from '@/lib/logger'

const RELOAD_GUARD_KEY = 'vidhyantra_stuck_loader_reload_ts'
/** Prevent reload loops if bootstrap stays broken after refresh. */
const RELOAD_COOLDOWN_MS = 60_000

export function clearStuckLoaderReloadGuard() {
  try {
    sessionStorage.removeItem(RELOAD_GUARD_KEY)
  } catch {
    // ignore
  }
}

/**
 * Full page reload if `isLoading` stays true (e.g. post-login bootstrap race).
 * Skips a second reload within {@link RELOAD_COOLDOWN_MS}.
 */
export function useStuckLoadingReload(isLoading: boolean, delayMs = 30_000) {
  const scheduledRef = useRef(false)

  useEffect(() => {
    if (!isLoading) {
      scheduledRef.current = false
      return
    }

    const timer = window.setTimeout(() => {
      if (scheduledRef.current) return
      scheduledRef.current = true

      try {
        const last = sessionStorage.getItem(RELOAD_GUARD_KEY)
        const now = Date.now()
        if (last) {
          const elapsed = now - Number.parseInt(last, 10)
          if (Number.isFinite(elapsed) && elapsed < RELOAD_COOLDOWN_MS) {
            logger.warn('[Loader] Stuck loading — reload skipped (cooldown active)')
            return
          }
        }
        sessionStorage.setItem(RELOAD_GUARD_KEY, String(now))
      } catch {
        // still attempt reload
      }

      logger.warn('[Loader] Stuck loading — reloading page')
      window.location.reload()
    }, delayMs)

    return () => window.clearTimeout(timer)
  }, [isLoading, delayMs])
}
